import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface AdjustedOrder {
  id: string;
  orderNumber: string;
  companyName: string;
  totalAmount: number;
  invoiceModifiedAt: string;
  status: string;
  paymentStatus: string;
}

export default function InvoicesAdjustedPage() {
  const [orders, setOrders] = useState<AdjustedOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdjusted();
  }, []);

  const fetchAdjusted = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_auth_token');
      const API_BASE = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${API_BASE}/orders/invoices-adjusted`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setOrders(data || []);
    } catch (err) {
      console.error('Failed to load adjusted invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeOrder = async (orderId: string) => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const API_BASE = import.meta.env.VITE_API_URL || '/api';
      await fetch(`${API_BASE}/orders/${orderId}/acknowledge-invoice-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) {
      console.error('Failed to acknowledge:', err);
    }
  };

  const acknowledgeAll = async () => {
    for (const order of orders) {
      await acknowledgeOrder(order.id);
    }
  };

  const fmt = (n: number) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices Adjusted</h1>
          <p className="text-sm text-gray-500 mt-1">
            Invoices that have been manually edited. Review and acknowledge to clear the alert.
          </p>
        </div>
        {orders.length > 0 && (
          <button
            onClick={acknowledgeAll}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
          >
            Acknowledge All ({orders.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <p className="text-green-700 font-medium text-lg">All clear</p>
          <p className="text-green-600 text-sm mt-1">No invoices have been adjusted since last review.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-amber-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-amber-800 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-amber-800 uppercase">Company</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-amber-800 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-amber-800 uppercase">Modified</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-amber-800 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-amber-800 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map(order => (
                <tr key={order.id} className="hover:bg-amber-50/50">
                  <td className="px-4 py-3 text-sm font-medium">
                    <Link to={`/orders/${order.orderNumber}`} className="text-blue-600 hover:underline">
                      {order.orderNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{order.companyName}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{fmt(order.totalAmount)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {order.invoiceModifiedAt ? new Date(order.invoiceModifiedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{order.paymentStatus}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => acknowledgeOrder(order.id)}
                      className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full hover:bg-amber-200 transition-colors"
                    >
                      Acknowledge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
